/**
 * ServiceDetailModal.js - Modal para gerir fluxo completo de reparações
 * CG Business ERP
 * 
 * Funcionalidades:
 * - Visualização de detalhes do serviço
 * - Pagamento de diagnóstico
 * - Gestão de peças do inventário
 * - Definição de mão de obra
 * - Envio e aprovação de cotação
 * - Registo de pagamentos (P0-CORE: PaymentPanel com split payment)
 * - Conclusão e facturação
 * - Menu de impressão fixo (P0-CORE)
 */

import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  Wrench, User, Phone, Mail, Laptop, Clock, 
  CreditCard, Plus, Minus, Trash2, CheckCircle, XCircle,
  FileText, DollarSign, Package, Send, AlertTriangle, 
  Receipt, Download, Banknote, Smartphone, Timer, Settings
} from 'lucide-react';
import { formatCurrency, formatDateTime, formatDisplayCode, cn } from '@/lib/utils';
import { servicesAPI, settingsAPI, printAPI } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

// P0-CORE: Import new components
import PaymentPanel from './PaymentPanel';
import ServicePrintMenu from './ServicePrintMenu';

// Status colors and labels
const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  reception: 'bg-cyan-100 text-cyan-800 border-cyan-300',
  diagnosis: 'bg-purple-100 text-purple-800 border-purple-300',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
  awaiting_parts: 'bg-orange-100 text-orange-800 border-orange-300',
  awaiting_approval: 'bg-amber-100 text-amber-800 border-amber-300',
  ready_for_pickup: 'bg-green-100 text-green-800 border-green-300',
  completed: 'bg-slate-100 text-slate-800 border-slate-300',
  cancelled: 'bg-red-100 text-red-800 border-red-300'
};

const statusLabels = {
  pending: 'Pendente',
  reception: 'Em Recepção',
  diagnosis: 'Em Diagnóstico',
  in_progress: 'Em Progresso',
  awaiting_parts: 'Aguarda Peças',
  awaiting_approval: 'Aguarda Aprovação',
  ready_for_pickup: 'Pronto p/ Levantar',
  completed: 'Concluído',
  cancelled: 'Cancelado'
};

// Workflow step indicator
const WORKFLOW_STEPS = [
  { key: 'reception', label: 'Recepção', icon: User },
  { key: 'diagnostic', label: 'Diagnóstico', icon: Settings },
  { key: 'quotation', label: 'Cotação', icon: FileText },
  { key: 'approval', label: 'Aprovação', icon: CheckCircle },
  { key: 'repair', label: 'Reparação', icon: Wrench },
  { key: 'payment', label: 'Pagamento', icon: CreditCard },
  { key: 'complete', label: 'Conclusão', icon: Receipt }
];

export default function ServiceDetailModal({ 
  open, 
  onOpenChange, 
  serviceId, 
  onServiceUpdated 
}) {
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [availableParts, setAvailableParts] = useState([]);
  const [partsSearch, setPartsSearch] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Form states
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [diagnosticPaymentMethod, setDiagnosticPaymentMethod] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [labourCost, setLabourCost] = useState('');
  const [labourDescription, setLabourDescription] = useState('');
  const [vatEnabled, setVatEnabled] = useState(false);
  const [vatRate, setVatRate] = useState(16);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  
  const { user, hasRole, defaultDocumentLocationId } = useAuth();
  const isAdmin = hasRole(['admin']);
  const canEdit = hasRole(['admin', 'store_manager', 'technician']);

  // Load service details
  const loadService = useCallback(async () => {
    if (!serviceId) return;
    try {
      setLoading(true);
      const response = await servicesAPI.get(serviceId);
      setService(response.data);
    } catch (error) {
      toast.error('Erro ao carregar detalhes do serviço');
    } finally {
      setLoading(false);
    }
  }, [serviceId]);

  // Load payment accounts
  const loadPaymentMethods = async () => {
    try {
      const response = await settingsAPI.listActivePaymentAccounts();
      setPaymentMethods(response.data || []);
    } catch (error) {
      console.error('Failed to load payment accounts:', error);
    }
  };

  // Search parts
  const searchParts = useCallback(async (query) => {
    if (!service?.location_id) {
      setAvailableParts([]);
      return;
    }

    try {
      const response = await servicesAPI.getAvailableParts(query, service?.location_id, true);
      setAvailableParts(response.data || []);
    } catch (error) {
      console.error('Failed to search parts:', error);
    }
  }, [service?.location_id]);

  useEffect(() => {
    if (open && serviceId) {
      loadService();
      loadPaymentMethods();
    }
  }, [open, serviceId, loadService]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (partsSearch) {
        searchParts(partsSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [partsSearch, searchParts]);

  useEffect(() => {
    if (!service) return;
    const quotation = service.quotation || {};
    setVatEnabled(Boolean(quotation.vat_enabled || quotation.apply_vat));
    setVatRate(Number(quotation.vat_rate ?? 16));
  }, [service]);

  // Get current workflow step
  const getCurrentStep = () => {
    if (!service) return 0;
    const status = service.status;
    const quotation = service.quotation;
    
    if (status === 'completed') return 7;
    if (status === 'ready_for_pickup') return 6;
    if (status === 'in_progress' && quotation?.customer_approved) return 5;
    if (status === 'awaiting_approval') return 4;
    if (quotation?.diagnostic_paid) return 3;
    if (status === 'reception' || status === 'diagnosis') return 2;
    return 1;
  };

  // Calculate totals
  const calculateTotals = () => {
    const quotation = service?.quotation || {};
    const diagnosticCost = quotation.diagnostic_cost || service?.diagnostic_cost || 0;
    const labourCost = quotation.labour_cost || 0;
    const partsTotal = quotation.parts_total || 0;
    const subtotal = diagnosticCost + labourCost + partsTotal;
    const discount = quotation.discount || 0;
    const taxableBase = Math.max(0, subtotal - discount);
    const vatActive = Boolean(vatEnabled);
    const vatRateValue = Number(vatRate ?? quotation.vat_rate ?? 16);
    const vatAmount = vatActive ? (taxableBase * vatRateValue) / 100 : 0;
    const total = taxableBase + vatAmount;
    const totalPaid = service?.total_paid || 0;
    const balance = Math.max(0, total - totalPaid);
    
    return { diagnosticCost, labourCost, partsTotal, subtotal, discount, taxableBase, vatActive, vatRateValue, vatAmount, total, totalPaid, balance };
  };

  const canIssueDeliveryGuide = service && (
    service.status === 'completed' ||
    service.status === 'ready_for_pickup' ||
    Number(service.balance_due ?? calculateTotals().balance) <= 0
  );

  // ========== ACTION HANDLERS ==========

  // Print PDF via Template Builder / runtime real
  const handlePrint = async (type) => {
    try {
      let payload = null;

      if (type === 'reception') {
        payload = {
          doc_type: 'reception_guide',
          service_id: service.id,
          location_id: defaultDocumentLocationId || service?.location_id || null,
          module_group: 'SERVIÇOS',
          copy_type: 'Original',
        };
      } else if (type === 'delivery') {
        payload = {
          doc_type: 'service_guide',
          service_id: service.id,
          location_id: defaultDocumentLocationId || service?.location_id || null,
          module_group: 'SERVIÇOS',
          copy_type: 'Original',
        };
      } else if (type === 'quotation') {
        payload = {
          doc_type: 'quotation',
          service_id: service.id,
          location_id: defaultDocumentLocationId || service?.location_id || null,
          module_group: 'DOCUMENTOS',
          copy_type: 'Original',
        };
      } else if (type === 'payment') {
        payload = {
          doc_type: 'payment_receipt',
          service_id: service.id,
          location_id: defaultDocumentLocationId || service?.location_id || null,
          module_group: 'SERVIÇOS',
          copy_type: 'Original',
        };
      }

      if (!payload) {
        toast.error('Documento não disponível');
        return;
      }

      const result = await printAPI.renderDocumentPdf(payload, 'download');
      if (!result?.success) {
        toast.error(result?.error || 'Documento não disponível');
        return;
      }

      toast.success('Documento gerado com sucesso');
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Erro ao abrir documento');
    }
  };

  // Pay diagnostic (compact form)
  const handleDiagnosticPayment = async () => {
    if (!diagnosticPaymentMethod) {
      toast.error('Seleccione um método de pagamento');
      return;
    }
    
    setProcessing(true);
    try {
      await servicesAPI.payDiagnostic(serviceId, diagnosticPaymentMethod, '', null, diagnosticPaymentMethod);
      toast.success('Diagnóstico pago com sucesso!');
      setDiagnosticPaymentMethod('');
      loadService();
      onServiceUpdated?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registar pagamento');
    } finally {
      setProcessing(false);
    }
  };

  // Pay diagnostic (original form)
  const handlePayDiagnostic = async () => {
    if (!selectedPaymentMethod) {
      toast.error('Seleccione um método de pagamento');
      return;
    }
    
    setProcessing(true);
    try {
      await servicesAPI.payDiagnostic(serviceId, selectedPaymentMethod, paymentReference, null, selectedPaymentMethod);
      toast.success('Diagnóstico pago com sucesso!');
      setSelectedPaymentMethod('');
      setPaymentReference('');
      loadService();
      onServiceUpdated?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registar pagamento');
    } finally {
      setProcessing(false);
    }
  };

  // Add part
  const handleAddPart = async (product) => {
    setProcessing(true);
    try {
      await servicesAPI.addPart(serviceId, product.id, 1);
      toast.success(`${product.name} adicionado`);
      loadService();
      onServiceUpdated?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar peça');
    } finally {
      setProcessing(false);
    }
  };

  // Remove part
  const handleRemovePart = async (productId) => {
    setProcessing(true);
    try {
      await servicesAPI.removePart(serviceId, productId);
      toast.success('Peça removida');
      loadService();
      onServiceUpdated?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover peça');
    } finally {
      setProcessing(false);
    }
  };

  // Update labour
  const handleUpdateLabour = async () => {
    if (!labourCost) {
      toast.error('Indique o custo de mão de obra');
      return;
    }
    
    setProcessing(true);
    try {
      await servicesAPI.updateLabour(serviceId, parseFloat(labourCost), labourDescription);
      toast.success('Mão de obra actualizada');
      loadService();
      onServiceUpdated?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao actualizar');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateVAT = async () => {
    setProcessing(true);
    try {
      await servicesAPI.updateQuotationVAT(serviceId, vatEnabled, Number(vatRate || 0));
      toast.success('Configuração de IVA actualizada');
      loadService();
      onServiceUpdated?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao actualizar IVA');
    } finally {
      setProcessing(false);
    }
  };

  // Send quotation
  const handleSendQuotation = async () => {
    setProcessing(true);
    try {
      await servicesAPI.sendQuotation(serviceId);
      toast.success('Cotação enviada ao cliente!');
      loadService();
      onServiceUpdated?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar cotação');
    } finally {
      setProcessing(false);
    }
  };

  // Process approval
  const handleApproval = async (approved) => {
    if (!approved && !rejectionReason.trim()) {
      toast.error('Indique o motivo da rejeição');
      return;
    }
    
    setProcessing(true);
    try {
      await servicesAPI.processApproval(serviceId, approved, rejectionReason);
      toast.success(approved ? 'Cliente aprovou a reparação!' : 'Cotação rejeitada pelo cliente');
      setRejectionReason('');
      loadService();
      onServiceUpdated?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar');
    } finally {
      setProcessing(false);
    }
  };

  // Register payment
  const handleRegisterPayment = async () => {
    if (!paymentAmount || !selectedPaymentMethod) {
      toast.error('Preencha valor e método de pagamento');
      return;
    }
    
    setProcessing(true);
    try {
      await servicesAPI.registerPayment(
        serviceId, 
        parseFloat(paymentAmount), 
        selectedPaymentMethod, 
        'final', 
        paymentReference, 
        null
      );
      toast.success('Pagamento registado!');
      setPaymentAmount('');
      setSelectedPaymentMethod('');
      setPaymentReference('');
      loadService();
      onServiceUpdated?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registar pagamento');
    } finally {
      setProcessing(false);
    }
  };

  // Complete and invoice
  const handleCompleteAndInvoice = async () => {
    setProcessing(true);
    try {
      const response = await servicesAPI.completeAndInvoice(serviceId, defaultDocumentLocationId);
      toast.success(`Serviço concluído! Factura: ${formatDisplayCode(response.data.invoice_number, 'INV')}`);
      loadService();
      onServiceUpdated?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao concluir serviço');
    } finally {
      setProcessing(false);
    }
  };

  // Download PDF
  const handleDownloadPdf = async () => {
    try {
      const result = await printAPI.renderDocumentPdf({
        doc_type: 'invoice',
        service_id: serviceId,
        location_id: defaultDocumentLocationId || service?.location_id || null,
        module_group: 'SERVIÇOS',
        copy_type: 'Original',
      }, 'download');
      if (!result?.success) {
        toast.error(result?.error || 'Erro ao descarregar PDF');
      }
    } catch (error) {
      toast.error('Erro ao descarregar PDF');
    }
  };

  if (!open) return null;

  const totals = service ? calculateTotals() : {};
  const currentStep = getCurrentStep();
  const quotation = service?.quotation || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(80vw,1360px)] max-w-[1360px] h-[88vh] overflow-hidden flex flex-col">
        {/* Header compacto */}
        <DialogHeader className="pb-2 border-b">
          <DialogDescription className="sr-only">
            Detalhes completos do serviço, incluindo recepção, diagnóstico, cotação, peças, pagamentos e opções de impressão.
          </DialogDescription>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Wrench className="h-4 w-4 text-orange-600" />
              {formatDisplayCode(service?.service_number, service?.service_type === 'warranty_claim' ? 'WAR' : 'SRV') || 'Carregando...'}
            </DialogTitle>
            {service && (
              <div className="flex items-center gap-2">
                <Badge className={cn("border text-xs py-0", statusColors[service.status])}>
                  {statusLabels[service.status]}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {service.device_brand} {service.device_model}
                </span>
              </div>
            )}
          </div>
          
          {/* P0-CORE: Totais + Menu de Impressão Fixo */}
          {service && (
            <div className="flex items-center justify-between bg-slate-50 rounded-md px-3 py-1.5 mt-2 text-sm">
              <PaymentPanel 
                serviceId={serviceId} 
                service={service} 
                compact={true}
              />
              <ServicePrintMenu 
                service={service} 
                disabled={processing}
              />
            </div>
          )}
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
          </div>
        ) : service ? (
          <div className="flex-1 min-h-0 overflow-hidden">
            {/* Workflow Progress compacto */}
            <div className="py-2 px-1">
              <div className="flex items-center justify-between">
                {WORKFLOW_STEPS.map((step, idx) => {
                  const isComplete = idx < currentStep;
                  const isCurrent = idx === currentStep - 1;
                  const StepIcon = step.icon;
                  return (
                    <div key={step.key} className="flex flex-col items-center flex-1">
                      <div className={cn(
                        "w-6 h-6 rounded-full flex items-center justify-center",
                        isComplete ? "bg-green-500 text-white" : 
                        isCurrent ? "bg-orange-500 text-white" : 
                        "bg-slate-200 text-slate-500"
                      )}>
                        <StepIcon className="h-3 w-3" />
                      </div>
                      <span className={cn(
                        "text-[10px] mt-0.5 text-center",
                        isCurrent ? "font-bold text-orange-600" : "text-muted-foreground"
                      )}>
                        {step.label}
                      </span>
                    </div>
                  );
                })}
              </div>
              <Progress value={(currentStep / WORKFLOW_STEPS.length) * 100} className="mt-1 h-0.5" />
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex h-full min-h-0 flex-col">
              <TabsList className="grid w-full grid-cols-4 h-8">
                <TabsTrigger value="details" className="text-xs py-1">Detalhes</TabsTrigger>
                <TabsTrigger value="quotation" className="text-xs py-1">Cotação</TabsTrigger>
                <TabsTrigger value="parts" className="text-xs py-1">Peças</TabsTrigger>
                <TabsTrigger value="payments" className="text-xs py-1">Pagamentos</TabsTrigger>
              </TabsList>

              {/* Details Tab - Layout 2 colunas compacto */}
              <TabsContent value="details" className="mt-2 flex-1 min-h-0 overflow-y-auto pr-1">
                <div className="grid grid-cols-3 gap-2">
                  {/* Customer Info - Compacto */}
                  <Card className="col-span-1">
                    <CardHeader className="py-2 px-3">
                      <CardTitle className="text-xs flex items-center gap-1">
                        <User className="h-3 w-3 text-blue-500" />
                        Cliente
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="py-2 px-3 space-y-0.5 text-xs">
                      <p className="font-medium truncate">{service.customer_name}</p>
                      <p className="flex items-center gap-1 text-muted-foreground">
                        <Phone className="h-2.5 w-2.5" />
                        {service.customer_phone}
                      </p>
                      {service.customer_email && (
                        <p className="flex items-center gap-1 text-muted-foreground truncate">
                          <Mail className="h-2.5 w-2.5" />
                          {service.customer_email}
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Device Info - Compacto */}
                  <Card className="col-span-1">
                    <CardHeader className="py-2 px-3">
                      <CardTitle className="text-xs flex items-center gap-1">
                        <Laptop className="h-3 w-3 text-purple-500" />
                        Equipamento
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="py-2 px-3 space-y-0.5 text-xs">
                      <p className="font-medium truncate">{service.device_brand} {service.device_model}</p>
                      <p className="text-muted-foreground">Cat: {service.device_category}</p>
                      {service.device_serial && (
                        <p className="text-muted-foreground truncate">SN: {service.device_serial}</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Service Info - Compacto */}
                  <Card className="col-span-1">
                    <CardHeader className="py-2 px-3">
                      <CardTitle className="text-xs flex items-center gap-1">
                        <Clock className="h-3 w-3 text-green-500" />
                        Info
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="py-2 px-3 space-y-0.5 text-xs">
                      <p className="font-medium">{formatDateTime(service.created_at)}</p>
                      <p className="text-muted-foreground">
                        Diag: {service.diagnostic_type === 'complex' ? 'Complexo' : service.diagnostic_type === 'no_charge' ? 'Não Pago' : 'Simples'}
                      </p>
                      <p className="text-muted-foreground">{formatCurrency(service.diagnostic_cost || 0)}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Issue Description - Compacto */}
                <Card className="mt-2">
                  <CardHeader className="py-2 px-3">
                    <CardTitle className="text-xs">Problema Reportado</CardTitle>
                  </CardHeader>
                  <CardContent className="py-2 px-3">
                    <p className="text-xs text-muted-foreground">{service.issue_description}</p>
                  </CardContent>
                </Card>

                {/* Diagnostic Payment Section - Compacto */}
                {!quotation.diagnostic_paid && service.status !== 'completed' && (
                  <Card className="mt-2 border-amber-300 bg-amber-50" data-testid="diagnostic-payment-pending-card">
                    <CardHeader className="py-2 px-3">
                      <CardTitle className="text-xs flex items-center gap-1 text-amber-800">
                        <AlertTriangle className="h-3 w-3" />
                        Pagamento de Diagnóstico Pendente
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="py-2 px-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-amber-700">
                            {formatCurrency(service.diagnostic_cost || 0)}
                          </span>
                          <Select value={diagnosticPaymentMethod} onValueChange={setDiagnosticPaymentMethod}>
                            <SelectTrigger className="w-32 h-7 text-xs" data-testid="diagnostic-payment-method-select">
                              <SelectValue placeholder="Conta" />
                            </SelectTrigger>
                            <SelectContent>
                              {paymentMethods.map(pm => (
                                <SelectItem key={pm.id} value={pm.id} className="text-xs">
                                  {pm.name}{pm.payment_method_name ? ` (${pm.payment_method_name})` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button 
                          size="sm"
                          className="h-7 text-xs bg-amber-600 hover:bg-amber-700"
                          onClick={handleDiagnosticPayment}
                          disabled={processing || !diagnosticPaymentMethod}
                          data-testid="diagnostic-payment-submit-btn"
                        >
                          <CreditCard className="h-3 w-3 mr-1" />
                          Pagar
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Quotation Tab */}
              <TabsContent value="quotation" className="mt-2 flex-1 min-h-0 overflow-y-auto pr-2 pb-4" data-testid="service-quotation-tab-content">
                <div className="grid gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.85fr)]">
                  <div className="space-y-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Wrench className="h-4 w-4" />
                          Mão de Obra
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="grid gap-3 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label>Custo (MZN)</Label>
                            <Input 
                              type="number"
                              placeholder="0.00"
                              value={labourCost || quotation.labour_cost || ''}
                              onChange={(e) => setLabourCost(e.target.value)}
                              disabled={service.status === 'completed'}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Descrição</Label>
                            <Input 
                              placeholder="Descrição do trabalho"
                              value={labourDescription || quotation.labour_description || ''}
                              onChange={(e) => setLabourDescription(e.target.value)}
                              disabled={service.status === 'completed'}
                            />
                          </div>
                        </div>
                        {service.status !== 'completed' && (
                          <Button size="sm" onClick={handleUpdateLabour} disabled={processing} data-testid="update-labour-btn">
                            Actualizar Mão de Obra
                          </Button>
                        )}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">IVA</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center justify-between gap-3">
                          <Label htmlFor="quotation-vat-enabled">Aplicar IVA na cotação</Label>
                          <input
                            id="quotation-vat-enabled"
                            type="checkbox"
                            checked={vatEnabled}
                            onChange={(e) => setVatEnabled(e.target.checked)}
                            disabled={service.status === 'completed'}
                            data-testid="quotation-vat-enabled-checkbox"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Taxa IVA (%)</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={vatRate}
                            onChange={(e) => setVatRate(e.target.value)}
                            disabled={service.status === 'completed' || !vatEnabled}
                            data-testid="quotation-vat-rate-input"
                          />
                        </div>
                        {service.status !== 'completed' && (
                          <Button size="sm" variant="outline" onClick={handleUpdateVAT} disabled={processing} data-testid="quotation-vat-save-button">
                            Guardar IVA
                          </Button>
                        )}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Package className="h-4 w-4" />
                          Peças ({(quotation.parts || []).length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {(quotation.parts || []).length === 0 ? (
                          <p className="text-sm text-muted-foreground py-4 text-center">
                            Nenhuma peça adicionada. Use a tab &ldquo;Peças&rdquo; para adicionar.
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {(quotation.parts || []).map((part, idx) => (
                              <div key={idx} className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 p-2.5">
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-medium">{part.product_name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {formatCurrency(part.unit_price)} × {part.quantity}
                                  </p>
                                </div>
                                <p className="shrink-0 font-medium">{formatCurrency(part.total)}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  <div className="space-y-4 xl:sticky xl:top-0">
                    <Card className="border-2 border-orange-200" data-testid="service-quotation-summary-card">
                      <CardContent className="pt-4">
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Diagnóstico</span>
                            <span>{formatCurrency(totals.diagnosticCost)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Mão de Obra</span>
                            <span>{formatCurrency(totals.labourCost)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Peças</span>
                            <span>{formatCurrency(totals.partsTotal)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>IVA {totals.vatActive ? `(${totals.vatRateValue}%)` : '(não aplicado)'}</span>
                            <span>{formatCurrency(totals.vatAmount || 0)}</span>
                          </div>
                          <Separator />
                          <div className="flex justify-between font-bold text-lg">
                            <span>Total</span>
                            <span className="text-green-600">{formatCurrency(totals.total)}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {quotation.diagnostic_paid && !quotation.quotation_sent && service.status !== 'completed' && (
                      <Button 
                        className="w-full bg-orange-600 hover:bg-orange-700"
                        onClick={handleSendQuotation}
                        disabled={processing}
                        data-testid="send-quotation-btn"
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Enviar Cotação ao Cliente
                      </Button>
                    )}

                    {quotation.quotation_sent && !quotation.customer_approved && !quotation.customer_rejected && (
                      <Card className="border-amber-300 bg-amber-50" data-testid="service-quotation-approval-card">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm text-amber-800">
                            Aguardando Aprovação do Cliente
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="flex flex-col gap-2 sm:flex-row">
                            <Button 
                              className="flex-1 bg-green-600 hover:bg-green-700"
                              onClick={() => handleApproval(true)}
                              disabled={processing}
                              data-testid="approve-quotation-btn"
                            >
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Cliente Aprovou
                            </Button>
                            <Button 
                              variant="destructive"
                              className="flex-1"
                              onClick={() => handleApproval(false)}
                              disabled={processing || !rejectionReason.trim()}
                              data-testid="reject-quotation-btn"
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Cliente Rejeitou
                            </Button>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-amber-700">Motivo da Rejeição (se aplicável)</Label>
                            <Textarea 
                              placeholder="Motivo..."
                              value={rejectionReason}
                              onChange={(e) => setRejectionReason(e.target.value)}
                              rows={2}
                            />
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {quotation.customer_approved && (
                      <div className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-100 p-3" data-testid="service-quotation-approved-banner">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <span className="text-green-800 font-medium">
                          Cliente aprovou em {quotation.customer_approved_at?.slice(0, 10)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Parts Tab */}
              <TabsContent value="parts" className="space-y-4 mt-4 flex-1 min-h-0 overflow-y-auto pr-1">
                {/* Search Parts */}
                {service.status !== 'completed' && (
                  <div className="space-y-2">
                    <Label>Procurar Peças no Inventário</Label>
                    <p className="text-xs text-muted-foreground" data-testid="service-parts-location-scope-hint">
                      Pesquisa limitada à localização do serviço: <strong>{service.location_name || 'N/A'}</strong>
                    </p>
                    <Input 
                      placeholder="Nome ou SKU da peça..."
                      value={partsSearch}
                      onChange={(e) => setPartsSearch(e.target.value)}
                      data-testid="service-parts-search-input"
                    />
                    
                    {availableParts.length > 0 && (
                      <div className="border rounded-lg max-h-[200px] overflow-y-auto">
                        {availableParts.map(part => (
                          <div 
                            key={part.id}
                            className="flex justify-between items-center p-2 hover:bg-slate-50 border-b last:border-b-0"
                          >
                            <div>
                              <p className="text-sm font-medium">{part.name}</p>
                              <p className="text-xs text-muted-foreground">
                                SKU: {part.sku} | Stock: {part.stock}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{formatCurrency(part.unit_price)}</span>
                              <Button 
                                size="sm"
                                onClick={() => handleAddPart(part)}
                                disabled={processing || part.stock < 1}
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {partsSearch.length >= 2 && availableParts.length === 0 && (
                      <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="service-parts-no-stock-message">
                        Sem stock disponível nesta localização. Faça transferência de stock para <strong>{service.location_name || 'a loja do serviço'}</strong> antes de adicionar a peça.
                      </div>
                    )}
                  </div>
                )}

                {/* Added Parts */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Peças Adicionadas</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(quotation.parts || []).length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">
                        Nenhuma peça adicionada
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {(quotation.parts || []).map((part, idx) => (
                          <div key={idx} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                            <div className="flex-1">
                              <p className="font-medium">{part.product_name}</p>
                              <p className="text-sm text-muted-foreground">
                                {formatCurrency(part.unit_price)} × {part.quantity} = {formatCurrency(part.total)}
                              </p>
                            </div>
                            {service.status !== 'completed' && (
                              <Button 
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => handleRemovePart(part.product_id)}
                                disabled={processing}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Separator />
                        <div className="flex justify-between font-bold pt-2">
                          <span>Total Peças</span>
                          <span>{formatCurrency(totals.partsTotal)}</span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Payments Tab - P0-CORE: Usando PaymentPanel com split payment */}
              <TabsContent value="payments" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1">
                <PaymentPanel
                  serviceId={serviceId}
                  service={service}
                  onPaymentRegistered={() => {
                    loadService();
                    onServiceUpdated?.();
                  }}
                  disabled={service.status === 'completed'}
                />

                {/* Complete & Invoice */}
                {totals.balance <= 0 && service.status !== 'completed' && quotation.customer_approved && (
                  <Button 
                    className="w-full h-12 text-lg bg-green-600 hover:bg-green-700 mt-4"
                    onClick={handleCompleteAndInvoice}
                    disabled={processing}
                    data-testid="complete-invoice-btn"
                  >
                    <Receipt className="h-5 w-5 mr-2" />
                    Concluir e Gerar Factura
                  </Button>
                )}

                {/* Download Invoice PDF */}
                {service.status === 'completed' && service.invoice_number && (
                  <Button 
                    variant="outline"
                    className="w-full mt-4"
                    onClick={handleDownloadPdf}
                    data-testid="download-invoice-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Descarregar Factura ({formatDisplayCode(service.invoice_number, 'INV')})
                  </Button>
                )}
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-8">
            Serviço não encontrado
          </p>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {/* Print Options - P0-TB2: Use authenticated PDF download */}
          {service && (
            <div className="flex gap-2 mr-auto">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handlePrint('reception')}
                data-testid="service-print-reception-btn"
              >
                <Download className="h-4 w-4 mr-1" />
                Guia Recepção
              </Button>
              {canIssueDeliveryGuide && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handlePrint('delivery')}
                  data-testid="service-print-delivery-btn"
                >
                  <FileText className="h-4 w-4 mr-1" />
                  Guia Entrega
                </Button>
              )}
            </div>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
